import { Controller } from "@hotwired/stimulus"
import Chart from "chart.js/auto"
import TomSelect from "tom-select"

export default class extends Controller {
    static targets = [
        "badgeZone",
        "badgeCreator",
        "badgeImpression",
        "badgeChart",
        "badgeEnrollmentsLink",
        "badgeTitleCount",

        "investigationZone",
        "investigationCreator",
        "investigationImpression",
        "investigationChart",
        "investigationEnrollmentsLink",
        "investigationTitleCount",

        "creationCreator",
        "creationChart",
        "creationEnrollmentsLink",

        "pdfExport"
    ]

    connect() {
        this.initTomSelects()
        this.bindEvents()

        this.loadBadgeChart()
        this.loadInvestigationChart()
        this.loadCreationChart()

        this.refreshEnrollmentsLinks()
    }

    disconnect() {
        if (this.badgeZoneSelect) this.badgeZoneSelect.destroy()
        if (this.investigationZoneSelect) this.investigationZoneSelect.destroy()
    }

    // ==========================================================
    // EVENTS
    // ==========================================================

    bindEvents() {
        if (this.hasBadgeZoneTarget) {
            this.badgeZoneTarget.addEventListener("change", () => {
                this.refreshEnrollmentsLinks()
                this.loadBadgeChart()
            })
        }

        if (this.hasBadgeImpressionTarget) {
            this.badgeImpressionTarget.addEventListener("change", () => {
                this.refreshEnrollmentsLinks()
                this.loadBadgeChart()
            })
        }

        if (this.hasBadgeCreatorTarget) {
            this.badgeCreatorTarget.addEventListener("change", () => {
                this.refreshEnrollmentsLinks()
                this.updatePdfExportUrl()
                this.loadBadgeChart()
            })
        }

        if (this.hasInvestigationZoneTarget) {
            this.investigationZoneTarget.addEventListener("change", () => {
                this.refreshEnrollmentsLinks()
                this.updatePdfExportUrl()
                this.loadInvestigationChart()
            })
        }

        if (this.hasInvestigationCreatorTarget) {
            this.investigationCreatorTarget.addEventListener("change", () => {
                this.refreshEnrollmentsLinks()
                this.loadInvestigationChart()
            })
        }

        if (this.hasInvestigationImpressionTarget) {
            this.investigationImpressionTarget.addEventListener("change", () => {
                this.refreshEnrollmentsLinks()
                this.loadInvestigationChart()
            })
        }

        if (this.hasCreationCreatorTarget) {
            this.creationCreatorTarget.addEventListener("change", () => {
                this.refreshEnrollmentsLinks()
                this.loadCreationChart()
            })
        }
    }

    refreshEnrollmentsLinks() {
        this.updateBadgeEnrollmentsLink()
        this.updateInvestigationEnrollmentsLink()
        this.updateCreationEnrollmentsLink()
    }

    // ==========================================================
    // TOMSELECT
    // ==========================================================

    initTomSelects() {
        this.initBadgeZoneTomSelect()
        this.initInvestigationZoneTomSelect()
    }

    initBadgeZoneTomSelect() {
        if (!this.hasBadgeZoneTarget) return

        if (this.badgeZoneTarget.tomselect) {
            this. badgeZoneSelect = this.badgeZoneTarget.tomselect
            return
        }

        this.badgeZoneSelect = new TomSelect(this.badgeZoneTarget, {
            plugins: {
                remove_button: { title: "Supprimer" },
                checkbox_options: {
                    checkedClassNames: ["ts-checked"],
                    uncheckedClassNames: ["ts-unchecked"]
                }
            },
            hideSelected: false,
            closeAfterSelect: false,
            persist: false,

            onInitialize() {
                this.refreshOptions(false)
            },

            onChange: () => {
                this.refreshEnrollmentsLinks()
                this.loadBadgeChart()
            }
        })

        this.installToggleAll(this.badgeZoneSelect, () => {
            this.refreshEnrollmentsLinks()
            this.loadBadgeChart()
        })
    }

    initInvestigationZoneTomSelect() {
        if (!this.hasInvestigationZoneTarget) return

        if (this.investigationZoneTarget.tomselect) {
            this.investigationZoneSelect = this.investigationZoneTarget.tomselect
            return
        }

        this.investigationZoneSelect = new TomSelect(this.investigationZoneTarget, {
            plugins: {
                remove_button: { title: "Supprimer" },
                checkbox_options: {
                    checkedClassNames: ["ts-checked"],
                    uncheckedClassNames: ["ts-unchecked"]
                }
            },
            hideSelected: false,
            closeAfterSelect: false,
            persist: false,

            onInitialize() {
                this.refreshOptions(false)
            },

            onChange: () => {
                this.loadInvestigationChart()
            }
        })

        this.installToggleAll(this.investigationZoneSelect, () => {
            this.loadInvestigationChart()
        })
    }

    installToggleAll(selectInstance, onAfterChange) {
        if (!selectInstance.options["__toggle_all__"]) {
            selectInstance.addOption({
                value: "__toggle_all__",
                text: "Tout sélectionner"
            })
        }

        selectInstance.refreshOptions(false)

        selectInstance.on("item_add", function(value) {
            if (value !== "__toggle_all__") return

            const values = Object.keys(this.options).filter(v => v !== "__toggle_all__")
            const selected = this.items.filter(v => v !== "__toggle_all__")

            if (selected.length === values.length) {
                this.clear(true)
            } else {
                this.setValue(values, true)
            }

            this.removeItem("__toggle_all__", true)
            onAfterChange()
        })
    }

    // ==========================================================
    // FETCH
    // ==========================================================

    async fetchChartData(eventId, params) {
        const response = await fetch(
            `/backoffice/events/${eventId}/reporting_chart?${params.toString()}`
        )

        if (!response.ok) {
            throw new Error("Erreur lors du chargement des statistiques")
        }

        return await response.json()
    }

    // ==========================================================
    // BADGES
    // ==========================================================

    async loadBadgeChart() {
        if (!this.hasBadgeChartTarget) return

        this.updateBadgeEnrollmentsLink()

        const eventId = this.badgeChartTarget.dataset.eventId
        const params = new URLSearchParams()
        params.append("chart_type", "badges")

        const badgeZoneSelect = this.hasBadgeZoneTarget ? this.badgeZoneTarget.tomselect : null

        if (badgeZoneSelect) {
            const zoneIds = [].concat(badgeZoneSelect.getValue()).filter(v => v && v !== "__toggle_all__")
            zoneIds.forEach(zoneId => params.append("zone_ids[]", zoneId))
        }

        if (this.hasBadgeCreatorTarget && this.badgeCreatorTarget.value) {
            params.append("creator_id", this.badgeCreatorTarget.value)
        }

        if (this.hasBadgeImpressionTarget && this.badgeImpressionTarget.value !== "") {
            params.append("impression", this.badgeImpressionTarget.value)
        }

        const card = $("#badge-statistics-card");
        // 🔥 SHOW LOADER SUR LA CARD
        card.find(".chart-loader").removeClass("d-none");

        const data = await this.fetchChartData(eventId, params);
        this.renderBadgeChart(data);

        card.find(".chart-loader").addClass("d-none");
    }

    // ==========================================================
    // INVESTIGATIONS
    // ==========================================================

    async loadInvestigationChart() {
        if (!this.hasInvestigationChartTarget) return

        this.updateInvestigationEnrollmentsLink()

        const eventId = this.investigationChartTarget.dataset.eventId
        const params = new URLSearchParams()
        params.append("chart_type", "investigations")

        const investigationZoneSelect = this.hasInvestigationZoneTarget ? this.investigationZoneTarget.tomselect : null
        if (investigationZoneSelect) {
            const zoneIds = [].concat(investigationZoneSelect.getValue()).filter(v => v && v !== "__toggle_all__")
            zoneIds.forEach(zoneId => params.append("zone_ids[]", zoneId))
        }

        if (this.hasInvestigationCreatorTarget && this.investigationCreatorTarget.value) {
            params.append("creator_id", this.investigationCreatorTarget.value)
        }

        if (this.hasInvestigationImpressionTarget && this.investigationImpressionTarget.value !== "") {
            params.append("impression", this.investigationImpressionTarget.value)
        }

        const card = $("#investigation-statistics-card");
        // 🔥 SHOW LOADER SUR LA CARD
        card.find(".chart-loader").removeClass("d-none");

        const data = await this.fetchChartData(eventId, params)
        this.renderInvestigationChart(data)

        card.find(".chart-loader").addClass("d-none");
    }

    // ==========================================================
    // CREATIONS
    // ==========================================================

    async loadCreationChart() {
        if (!this.hasCreationChartTarget) return

        this.updateCreationEnrollmentsLink()

        const eventId = this.creationChartTarget.dataset.eventId
        const params = new URLSearchParams()
        params.append("chart_type", "creations")

        if (this.hasCreationCreatorTarget && this.creationCreatorTarget.value) {
            params.append("creator_id", this.creationCreatorTarget.value)
        }

        const card = $("#creation-statistics-card");
        // 🔥 SHOW LOADER SUR LA CARD
        card.find(".chart-loader").removeClass("d-none");

        const data = await this.fetchChartData(eventId, params)
        this.renderCreationChart(data)

        card.find(".chart-loader").addClass("d-none");
    }

    // ==========================================================
    // CHART RENDERING
    // ==========================================================

    renderBadgeChart(data) {
        const labels = data.map(row => `${row.category} (${row.total || 0})`)
        const printed = data.map(row => row.printed)
        const notPrinted = data.map(row => row.not_printed)
        const totalBadges = data.reduce((sum, row) => sum + (row.total || 0), 0)

        if (this.hasBadgeTitleCountTarget) {
            this.badgeTitleCountTarget.textContent = `(${totalBadges})`
        }

        if (this.badgeChart) this.badgeChart.destroy()

        this.badgeChart = new Chart(this.badgeChartTarget, {
            type: "bar",
            data: {
                labels,
                datasets: [
                    { label: "Imprimées", data: printed },
                    { label: "Non imprimées", data: notPrinted }
                ]
            },
            options: {
                responsive: true,
                scales: {
                    x: { stacked: true },
                    y: { stacked: true, beginAtZero: true }
                },
                plugins: {
                    tooltip: {
                        callbacks: {
                            footer: (tooltipItems) => {
                                const row = data[tooltipItems[0].dataIndex]
                                return `Total : ${row.total || 0}`
                            }
                        }
                    }
                }
            }
        })
    }

    renderInvestigationChart(data) {
        const labels = data.map(row => `${row.category} (${row.total || 0})`)
        const totalBadges = data.reduce((sum, row) => sum + (row.total || 0), 0)

        if (this.hasInvestigationTitleCountTarget) {
            this.investigationTitleCountTarget.textContent = `(${totalBadges})`
        }

        const statuses = [
            "investigation_init",
            "investigation_pending",
            "investigation_rejected",
            "investigation_validated",
            "investigation_refused",
            "investigation_freezed",
            "investigation_stopped"
        ]

        const statusLabels = {
            investigation_init: "Statut initial",
            investigation_pending: "En attente",
            investigation_rejected: "Informations manquantes",
            investigation_validated: "Préfecture OK",
            investigation_refused: "Préfecture KO",
            investigation_freezed: "Gelé",
            investigation_stopped: "Arrêté"
        }

        const datasets = statuses.map(status => ({
            label: statusLabels[status],
            data: data.map(row => row[status] || 0)
        }))

        if (this.investigationChart) this.investigationChart.destroy()

        this.investigationChart = new Chart(this.investigationChartTarget, {
            type: "bar",
            data: { labels, datasets },
            options: {
                responsive: true,
                scales: {
                    x: { stacked: true },
                    y: { stacked: true, beginAtZero: true }
                },
                plugins: {
                    tooltip: {
                        callbacks: {
                            footer: (tooltipItems) => {
                                const row = data[tooltipItems[0].dataIndex]
                                return `Total : ${row.total || 0}`
                            }
                        }
                    }
                }
            }
        })
    }

    renderCreationChart(data) {
        const labels = data.map(row => row.date)
        const values = data.map(row => row.count)

        if (this.creationChart) this.creationChart.destroy()

        this.creationChart = new Chart(this.creationChartTarget, {
            type: "line",
            data: {
                labels,
                datasets: [
                    {
                        label: "Créations",
                        data: values,
                        tension: 0.2
                    }
                ]
            },
            options: {
                responsive: true,
                scales: {
                    y: { beginAtZero: true }
                }
            }
        })
    }

    // ==========================================================
    // EXPORT PDF
    // ==========================================================

    exportPdf(event) {
        event.preventDefault()

        const eventId = this.element.dataset.eventId
        const form = document.createElement("form")

        form.method = "POST"
        form.action = `/backoffice/events/${eventId}/reporting_export_pdf.pdf`
        form.target = "_blank"
        form.style.display = "none"

        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
        if (csrfToken) {
            this.appendHiddenField(form, "authenticity_token", csrfToken)
        }

        const badgeZoneSelect = this.hasBadgeZoneTarget ? this.badgeZoneTarget.tomselect : null

        if (badgeZoneSelect) {
            [].concat(badgeZoneSelect.getValue())
                .filter(v => v && v !== "__toggle_all__")
                .forEach(zoneId => this.appendHiddenField(form, "badge_zone_ids[]", zoneId))
        }

        if (this.hasBadgeCreatorTarget && this.badgeCreatorTarget.value) {
            this.appendHiddenField(form, "badge_creator_id", this.badgeCreatorTarget.value)
        }

        if (this.hasBadgeImpressionTarget && this.badgeImpressionTarget.value !== "") {
            this.appendHiddenField(form, "badge_impression", this.badgeImpressionTarget.value)
        }

        const investigationZoneSelect = this.hasInvestigationZoneTarget ? this.investigationZoneTarget.tomselect : null

        if (investigationZoneSelect) {
            [].concat(investigationZoneSelect.getValue())
                .filter(v => v && v !== "__toggle_all__")
                .forEach(zoneId => this.appendHiddenField(form, "investigation_zone_ids[]", zoneId))
        }

        if (this.hasInvestigationCreatorTarget && this.investigationCreatorTarget.value) {
            this.appendHiddenField(form, "investigation_creator_id", this.investigationCreatorTarget.value)
        }

        if (this.hasInvestigationImpressionTarget && this.investigationImpressionTarget.value !== "") {
            this.appendHiddenField(form, "investigation_impression", this.investigationImpressionTarget.value)
        }

        if (this.hasCreationCreatorTarget && this.creationCreatorTarget.value) {
            this.appendHiddenField(form, "creation_creator_id", this.creationCreatorTarget.value)
        }

        if (this.badgeChart) {
            this.appendHiddenField(form, "badge_chart_image", this.badgeChart.toBase64Image())
        }

        if (this.investigationChart) {
            this.appendHiddenField(form, "investigation_chart_image", this.investigationChart.toBase64Image())
        }

        if (this.creationChart) {
            this.appendHiddenField(form, "creation_chart_image", this.creationChart.toBase64Image())
        }

        document.body.appendChild(form)
        form.submit()
        form.remove()
    }

    updatePdfExportUrl() {
        if (!this.hasPdfExportTarget) return

        const eventId = this.badgeChartTarget?.dataset.eventId ||
            this.investigationChartTarget?.dataset.eventId ||
            this.creationChartTarget?.dataset.eventId

        if (!eventId) return

        const params = new URLSearchParams()

        this.selectedValues(this.badgeZoneTarget).forEach(value => {
            params.append("badge_zone_ids[]", value)
        })

        this.selectedValues(this.investigationZoneTarget).forEach(value => {
            params.append("investigation_zone_ids[]", value)
        })

        if (this.hasCreatorProfileTarget && this.creatorProfileTarget.value) {
            params.append("badge_creator_id", this.creatorProfileTarget.value)
        }

        if (this.hasCreatorInvestigationTarget && this.creatorInvestigationTarget.value) {
            params.append("investigation_creator_id", this.creatorInvestigationTarget.value)
        }

        if (this.hasCreatorCreationTarget && this.creatorCreationTarget.value) {
            params.append("creation_creator_id", this.creatorCreationTarget.value)
        }

        if (this.hasImpressionTarget && this.impressionTarget.value) {
            params.append("badge_impression", this.impressionTarget.value)
            params.append("investigation_impression", this.impressionTarget.value)
        }

        this.pdfExportTarget.href =
            `/backoffice/events/${eventId}/reporting_export_pdf.pdf?${params.toString()}`
    }

    appendHiddenField(form, name, value) {
        const input = document.createElement("input")
        input.type = "hidden"
        input.name = name
        input.value = value
        form.appendChild(input)
    }

    // ==========================================================
    // ENROLLMENTS LINK
    // ==========================================================

    updateBadgeEnrollmentsLink() {
        if (!this.hasBadgeEnrollmentsLinkTarget) return

        const url = new URL(this.badgeEnrollmentsLinkTarget.href, window.location.origin)
        url.search = ""

        const eventId = this.element.dataset.eventId
        if (eventId) {
            url.searchParams.set("enrollments_grid[event_id]", eventId)
        }

        const badgeZoneSelect = this.hasBadgeZoneTarget ? this.badgeZoneTarget.tomselect : null
        if (badgeZoneSelect) {
            [].concat(badgeZoneSelect.getValue())
                .filter(v => v && v !== "__toggle_all__")
                .forEach(zoneId => url.searchParams.append("enrollments_grid[zone_id][]", zoneId))
        }

        if (this.hasBadgeCreatorTarget && this.badgeCreatorTarget.value) {
            url.searchParams.set("enrollments_grid[user_id]", this.badgeCreatorTarget.value)
        }

        const badgeImpressionValue =
            this.hasBadgeImpressionTarget
                ? this.booleanParamValue(this.badgeImpressionTarget.value)
                : null

        if (badgeImpressionValue !== null) {
            url.searchParams.set("enrollments_grid[ready_to_print]", badgeImpressionValue)
        }

        this.badgeEnrollmentsLinkTarget.href = url.toString()
    }

    updateInvestigationEnrollmentsLink() {
        if (!this.hasInvestigationEnrollmentsLinkTarget) return

        const url = new URL(this.investigationEnrollmentsLinkTarget.href, window.location.origin)
        url.search = ""

        const eventId = this.element.dataset.eventId
        if (eventId) {
            url.searchParams.set("enrollments_grid[event_id]", eventId)
        }

        const investigationZoneSelect = this.hasInvestigationZoneTarget ? this.investigationZoneTarget.tomselect : null
        if (investigationZoneSelect) {
            [].concat(investigationZoneSelect.getValue())
                .filter(v => v && v !== "__toggle_all__")
                .forEach(zoneId => url.searchParams.append("enrollments_grid[zone_id][]", zoneId))
        }

        const investigationImpressionValue =
            this.hasInvestigationImpressionTarget
                ? this.booleanParamValue(this.investigationImpressionTarget.value)
                : null

        if (investigationImpressionValue !== null) {
            url.searchParams.set("enrollments_grid[ready_to_print]", investigationImpressionValue)
        }

        if (this.hasInvestigationCreatorTarget && this.investigationCreatorTarget.value) {
            url.searchParams.set("enrollments_grid[user_id]", this.investigationCreatorTarget.value)
        }

        const badgeImpressionValue =
            this.hasBadgeImpressionTarget
                ? this.booleanParamValue(this.badgeImpressionTarget.value)
                : null

        if (badgeImpressionValue !== null) {
            url.searchParams.set("enrollments_grid[ready_to_print]", badgeImpressionValue)
        }

        this.investigationEnrollmentsLinkTarget.href = url.toString()
    }

    updateCreationEnrollmentsLink() {
        if (!this.hasCreationEnrollmentsLinkTarget) return

        const url = new URL(this.creationEnrollmentsLinkTarget.href, window.location.origin)
        url.search = ""

        const eventId = this.element.dataset.eventId
        if (eventId) {
            url.searchParams.set("enrollments_grid[event_id]", eventId)
        }

        if (this.hasCreatorCreationTarget && this.creatorCreationTarget.value) {
            url.searchParams.set("enrollments_grid[user_id]", this.creatorCreationTarget.value)
        }

        this.creationEnrollmentsLinkTarget.href = url.toString()
    }

    booleanParamValue(value) {
        if (value === "true") return "1"
        if (value === "false") return "0"
        return null
    }
}